// Turn a CITED knowledge entry into a real DESTINATION Otto can navigate to.
//
// A platform_qa answer cites typed KB keys (`page.project_settings_category`, `concept.fci`,
// `action.table_view.export`). For a PAGE citation the user's obvious next move is "just take me there" —
// but the key alone says nothing about where "there" is, which is why cited pages used to render as inert
// source chips. Two grounding routes, in this order:
//
//   1. The permission-scoped nav CATALOG (navTargets). Preferred whenever it has the page: the user is
//      known to be able to open it, and the target carries the breadcrumb trail on-screen navigation
//      produces. Matched on key SHAPE, not string identity — the KB slugs are snake_case
//      (`mobile_solutions`, `landing_page`, `smart_charts`) while navCatalog keys are kebab/camelCase
//      ("mobile-solutions", "landing-page", "smartCharts"), so `===` silently missed nearly every page.
//   2. The entry's own ROUTE (`deep_link`, else the scanner-recorded `generated_meta.route`, both supplied
//      by the backend as `qa_sources`). This is what reaches the ~50 settings/menu sub-pages the nav
//      catalog's first slice never covered — the Category page under Project Settings among them.
//
// CAVEAT, deliberate and narrow: route-grounded targets are NOT permission-scoped (only the nav catalog is).
// The answer that cited the page has already told the user where the feature lives, so the chip only saves
// them the click-path — but a user without rights to that page lands on it empty rather than being told no.
// Worth revisiting if/when ProjectSettings.jsx's permission-gated tab list is extracted into a shared
// builder (the filterFieldBuilders / navCatalog precedent), which would make route grounding scoped too.
//
// Pure + deterministic (no LLM, no network) — unit-tested, no eval gate.

// Fill-ins for a React-Router path parameter that names the project in scope. The KB carries both spellings
// (`:projectId` in navCatalog-derived deep links, `:id` in App.js routes).
const PROJECT_PARAMS = ["projectId", "project_id", "id"];

// Compare keys/labels by shape: lowercase, letters+digits only. "mobile-solutions" == "mobile_solutions".
export const normalizeKey = s =>
    String(s == null ? "" : s)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");

// The slug of a typed KB key: "page.project_settings_category" -> "project_settings_category".
export const slugOf = key => {
    const parts = String(key == null ? "" : key).split(".");
    return parts.length > 1 ? parts.slice(1).join(".") : "";
};

// A reachable nav target for this KB slug, or null. Key first, then the visible label (which is how a
// renamed-but-same-page target like "cr-recommendations"/"Capital Request" still matches).
export const findNavTarget = (slug, navTargets) => {
    const want = normalizeKey(slug);
    if (!want) return null;
    const list = navTargets || [];
    return list.find(t => t && normalizeKey(t.key) === want) || list.find(t => t && normalizeKey(t.label) === want) || null;
};

// Resolve a KB route into a URL we can actually push, or null.
// Routes are router PATTERNS: a project-scoped one carries a placeholder only the client can fill (it knows
// the scope). Anything still unresolved after that — another parameter (`:tab`, `:settingType`), an
// alternation/bundle route ("/{local|master}-…"), a non-absolute path — yields null: a half-resolved route
// is a dead link, and a dead link is worse than an inert chip (the grounding rule).
export const resolveRoute = (route, { projectId } = {}) => {
    const raw = String(route == null ? "" : route).trim();
    if (!raw || raw.charAt(0) !== "/") return null;
    if (raw.indexOf("{") >= 0 || raw.indexOf("*") >= 0) return null;
    let url = raw;
    if (projectId != null && String(projectId) !== "") {
        PROJECT_PARAMS.forEach(p => {
            url = url.split(`:${p}`).join(String(projectId));
        });
    }
    return /:[A-Za-z_]/.test(url) ? null : url;
};

// The destination for one cited entry, or null when it names nothing navigable (a concept/action, an
// unreachable page, a route we can't fully resolve). `source` is a `qa_sources` item:
// { key, entry_type, title, deep_link, route }. `label` is the chip's own text, used only as the fallback
// name when the entry has no title.
export const targetForSource = (source, { navTargets, projectId, label } = {}) => {
    if (!source || (source.entry_type || "") !== "page") return null;
    const slug = slugOf(source.key);
    const known = findNavTarget(slug, navTargets);
    if (known) return known;
    const url = resolveRoute(source.route || source.deep_link, { projectId });
    if (!url) return null;
    // Synthesized target, same shape onNavigate accepts from the catalog (no `bc`: we can't invent a
    // breadcrumb trail for a page the catalog doesn't model, and navExecutor treats an absent one as "leave
    // the trail alone").
    return { key: `qa:${slug}`, label: source.title || label || "", url };
};

// Index `qa_sources` by key for O(1) lookup while mapping citations.
export const sourcesByKey = qaSources => {
    const out = {};
    (qaSources || []).forEach(s => {
        if (s && s.key) out[s.key] = s;
    });
    return out;
};
